import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router';

import Money from '@/components/Money';
import TotalCard from '@/components/ui/TotalCard';
import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';
import { EnergyChart } from '@/features/dashboard/components/EnergyChart';
import type { EnergyChartPanelProps } from '@/features/dashboard/types';
import { setLinkNavigation } from '@/hooks/usePageTracking';
import { formatEnergyValue, stringifyQueryParams, t } from '@/lib/utils';
import { CHARGING_ACTIVITY } from '@/routes/Router';
import { useUserStore } from '@/stores/userStore';

import { EnergyChartSkeleton } from './EnergyChartSkeleton';

export const EnergyChartPanel = ({ sessions, timeFrame, isLoading }: EnergyChartPanelProps) => {
    const { currency } = useUserStore();
    const { locale } = useIntl();

    const { totalEnergyCharged: totalEnergy, totalChargingCost: totalCosts } = sessions || {};

    const { value: totalEnergyFormatted, unit: totalEnergyUnit } = useMemo(
        () => formatEnergyValue(totalEnergy ?? 0, locale),
        [totalEnergy, locale]
    );

    const timeParams = useMemo(
        () => ({
            startDate: timeFrame?.startDate?.toISOString(),
            endDate: timeFrame?.endDate?.toISOString(),
            timeFrame: timeFrame?.frame.timeEnum ?? '',
        }),
        [timeFrame]
    );

    if (isLoading) {
        return <EnergyChartSkeleton />;
    }

    if (!sessions?.chargingActivitySummaryList.length) {
        return (
            <NotFoundState
                headline={t('chargingSessions.noResultsFound.title')}
                message={t('chargingSessions.noResultsFound.description')}
                outerClassName='border-none'
            />
        );
    }

    const handleNavigationLink = () => {
        setLinkNavigation();
        trackCustomEvent(
            'open_charging_details_link',
            {
                trigger: 'click',
                flow_name: 'overview',
                element_name: 'charging_activity_link',
            },
            createDebugConfig()
        );
    };

    return (
        <div className='display-flex flex-column'>
            <div className='gap-15 display-flex'>
                <TotalCard type='dashboard' title={<FormattedMessage id='general.totalChargingCostsNet' />}>
                    <span className='text-bold text-color-black' style={{ fontSize: '3.75rem', lineHeight: '1' }}>
                        <Money currency={currency} value={totalCosts ?? 0} />
                    </span>
                </TotalCard>
                <div className='margin-y-25 border-width-1' />
                <TotalCard type='dashboard' title={<FormattedMessage id='general.energyCharged' />}>
                    <span className='text-color-black'>
                        <span className='text-bold text-color-black' style={{ fontSize: '3.75rem', lineHeight: '1' }}>
                            {totalEnergyFormatted}
                        </span>{' '}
                        {totalEnergyUnit}
                    </span>
                </TotalCard>
            </div>
            <div className='display-flex height-350'>
                <EnergyChart sessions={sessions} timeFrame={timeFrame} />
            </div>
            <div className='display-flex justify-content-end padding-x-20 padding-bottom-20'>
                <Link
                    className='text-color-primary'
                    to={{
                        pathname: CHARGING_ACTIVITY,
                        search: stringifyQueryParams(timeParams),
                    }}
                    onClick={handleNavigationLink}
                >
                    <FormattedMessage id='overview.seeDetails' />
                </Link>
            </div>
        </div>
    );
};
